import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

// Bundled English translations for critical pages (prevents loading failures)
const landingEN = {
  "hero": {
    "tagline": "Lawn Care Timed by Science, Not the Calendar",
    "title": "Time Your Lawn Care by <1>Growing Degree Days</1>, Not Guesswork",
    "titleSimple": "Time Your Lawn Care by Growing Degree Days, Not Guesswork",
    "whenWord": "Growing Degree Days",
    "description": "TurfPulse tracks real-time GDD for your location so you know exactly when to apply PGRs, pre-emergents, and turf treatments. Better timing. Fewer missed windows. Healthier turf.",
    "openTracker": "Get TurfPulse Free",
    "learnMore": "See How It Works"
  },
  "features": {
    "title": "Built for People Who Actually Manage Turf",
    "description": "TurfPulse uses Growing Degree Days (GDD) — the same science used by golf course superintendents — to tell you exactly when to treat. Based on real weather at your location, not generic regional averages.",
    "feature1": {
      "title": "Real-Time GDD Tracking",
      "description": "Actual daily high/low temperatures from your location, calculated into YTD Growing Degree Days — base 32 and base 50"
    },
    "feature2": {
      "title": "Smart Treatment Timing",
      "description": "Know exactly when crabgrass germinates, grubs emerge, or your PGR needs reapplication — based on heat, not the calendar"
    },
    "feature3": {
      "title": "Product Calculators",
      "description": "Built-in rate calculators for PGR, herbicide, and pest products. Log every application with GDD values and weather"
    },
    "feature4": {
      "title": "Zone Management",
      "description": "Track different areas of your property with unique conditions, thresholds, and treatment schedules"
    }
  },
  "pricing": {
    "title": "Simple, Transparent Pricing",
    "subtitle": "Choose the plan that fits your lawn care goals",
    "free": {
      "name": "Free",
      "price": "$0",
      "period": "forever",
      "description": "Perfect for getting started",
      "features": [
        "Basic GDD tracking",
        "PGR Department access",
        "Single property",
        "Weekly email updates"
      ],
      "cta": "Get Started Free"
    },
    "seasonal": {
      "name": "Homeowner Seasonal",
      "price": "$50",
      "period": "per season (6 months)",
      "description": "For dedicated homeowners",
      "features": [
        "Everything in Free",
        "Weed Control Department",
        "Equipment & Spreader logs",
        "Multiple zones",
        "Daily email alerts"
      ],
      "cta": "Start Seasonal"
    },
    "annual": {
      "name": "Enthusiast Annual",
      "price": "$80",
      "period": "per year",
      "description": "Full access to everything",
      "popular": "Most Popular",
      "features": [
        "Everything in Seasonal",
        "Pest & Disease Departments",
        "Soil Analysis integration",
        "5-Day Spray Forecast",
        "Unlimited zones",
        "Priority support"
      ],
      "cta": "Go Annual"
    }
  },
  "cta": {
    "title": "Ready to Time Your Lawn Care with Precision?",
    "description": "Join homeowners and turf enthusiasts who've replaced calendar guessing with GDD-based precision.",
    "button": "Get TurfPulse Free"
  },
  "footer": {
    "privacy": "Privacy Policy",
    "terms": "Terms of Service",
    "accessibility": "Accessibility",
    "disclosure": "Affiliate Disclosure",
    "copyright": "© 2026 TurfPulse. All rights reserved."
  },
  "auth": {
    "loginTitle": "Welcome Back",
    "loginSubtitle": "Sign in to your TurfPulse account",
    "registerTitle": "Join TurfPulse",
    "registerSubtitle": "Create your free account to get started",
    "email": "Email",
    "password": "Password",
    "name": "Name",
    "grassType": "Grass Type",
    "coolSeason": "Cool-Season (Fescue, Bluegrass, Rye)",
    "warmSeason": "Warm-Season (Bermuda, Zoysia, St. Augustine)",
    "login": "Sign In",
    "register": "Create Account",
    "forgotPassword": "Forgot password?",
    "noAccount": "Don't have an account?",
    "hasAccount": "Already have an account?",
    "signUp": "Sign up",
    "signIn": "Sign in",
    "magicLink": "Sign in with Magic Link",
    "orContinueWith": "Or continue with"
  }
};

const commonEN = {
  "appName": "TurfPulse",
  "loading": "Loading...",
  "error": "Error",
  "success": "Success",
  "save": "Save",
  "cancel": "Cancel",
  "delete": "Delete",
  "edit": "Edit",
  "add": "Add",
  "close": "Close",
  "submit": "Submit",
  "back": "Back",
  "next": "Next",
  "upgrade": "Upgrade",
  "logout": "Log Out",
  "login": "Log In",
  "register": "Register"
};

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: ['en', 'es', 'vi'],
    debug: false,
    
    // Pre-load English translations to prevent blank screens
    resources: {
      en: {
        landing: landingEN,
        common: commonEN
      }
    },
    
    // Still allow loading additional namespaces via HTTP
    partialBundledLanguages: true,
    
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'turfpulse_language',
    },
    
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    
    ns: ['common', 'nav', 'dashboard', 'auth', 'profile', 'learn', 'products', 'landing', 'property'],
    defaultNS: 'common',
    
    interpolation: {
      escapeValue: false,
    },
    
    react: {
      useSuspense: true,
    },
  });

export default i18n;

// Language display names
export const languages = {
  en: { name: 'English', flag: '🇺🇸', nativeName: 'English' },
  es: { name: 'Spanish', flag: '🇲🇽', nativeName: 'Español' },
  vi: { name: 'Vietnamese', flag: '🇻🇳', nativeName: 'Tiếng Việt' },
};
